<script setup lang="ts">
import { PersonStores, usePeopleTypesStore } from '@/store';
import { LocationFormInterface } from '@/types';
import { useToast } from 'primevue/usetoast';
import { PERMISSIONS } from '@/enum';
import { can } from '@/composables';
import { UserLocationForm } from '@/components/Forms';

/**
 * set up the toast
 */
const toast = useToast();

/**
 * All of the state references including the convertion of the
 * profile location state object into a ref.
 */
const profileStore = PersonStores.useUserProfileStore();

const PEOPLE_TYPES = usePeopleTypesStore().PEOPLE_TYPES;

/**
 * Handle save event emitted by the child component
 * in order to reset the user location data.
 */
const onSave = async (data: LocationFormInterface) => {
  const response = await profileStore.updateLocationInfo(data);
  if (response === 200) {
    doToast();
  } else {
    doToast(true);
  }
};

/**
 * Display the toast message.
 */
const doToast = (error = false) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? 'there was an error' : 'User Location information has been saved',
    life: 3000,
  });
};

const canUpdateLocation = () => {
  const personType: string = profileStore.person?.people_type?.acronym as string;
  let permissionString = 'PEOPLE_LOCATION_UPDATE';
  let str: keyof typeof PERMISSIONS;
  switch (personType) {
    case PEOPLE_TYPES.SUPPORT_ADMIN: {
      permissionString += '_SUPPORT_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SCHOOL_ADMIN: {
      permissionString += '_SCHOOL_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SUPER_ADMIN: {
      permissionString += '_SUPER_ADMIN';
      break;
    }
  }
  str = permissionString as keyof typeof PERMISSIONS;
  return can([PERMISSIONS[str]]);
};
</script>

<template>
  <UserLocationForm
    :permission-to-edit="canUpdateLocation()"
    :data="profileStore.location"
    :loading="(profileStore.loading as boolean)"
    @save="onSave"
  />
</template>
